import { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface OfficerDataFieldsProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const OfficerDataFields = ({ data, onChange }: OfficerDataFieldsProps) => {
  // Initialize default dates when component mounts
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const twoWeeksOut = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const updates: Record<string, any> = {};
    
    if (!data.effective_date) updates.effective_date = today;
    if (!data.board_approval_date) updates.board_approval_date = today;
    if (!data.acceptance_deadline) updates.acceptance_deadline = twoWeeksOut;
    
    if (Object.keys(updates).length > 0) {
      onChange({ ...data, ...updates });
    }
  }, []);

  const updateField = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="officer_full_name">
          Full Legal Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="officer_full_name"
          placeholder="Jane Elizabeth Smith"
          value={data.officer_full_name || ""}
          onChange={(e) => updateField("officer_full_name", e.target.value)}
          required
        />
      </div>

      <div>
        <Label htmlFor="officer_position">
          Officer Position <span className="text-destructive">*</span>
        </Label>
        <Select
          value={data.officer_position || ""}
          onValueChange={(value) => updateField("officer_position", value)}
        >
          <SelectTrigger id="officer_position">
            <SelectValue placeholder="Select position" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="President">President</SelectItem>
            <SelectItem value="Vice President">Vice President</SelectItem>
            <SelectItem value="Secretary">Secretary</SelectItem>
            <SelectItem value="Treasurer">Treasurer</SelectItem>
            <SelectItem value="Assistant Secretary">Assistant Secretary</SelectItem>
            <SelectItem value="Assistant Treasurer">Assistant Treasurer</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="officer_address">
          Residential Address <span className="text-destructive">*</span>
        </Label>
        <Input
          id="officer_address"
          placeholder="456 Executive Blvd"
          value={data.officer_address || ""}
          onChange={(e) => updateField("officer_address", e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="officer_city">City</Label>
          <Input
            id="officer_city"
            placeholder="Chicago"
            value={data.officer_city || ""}
            onChange={(e) => updateField("officer_city", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="officer_state">State / Province</Label>
          <Input
            id="officer_state"
            placeholder="IL"
            value={data.officer_state || ""}
            onChange={(e) => updateField("officer_state", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="officer_zip">ZIP / Postal Code</Label>
          <Input
            id="officer_zip"
            placeholder="60601"
            value={data.officer_zip || ""}
            onChange={(e) => updateField("officer_zip", e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="officer_country">Country</Label>
        <Input
          id="officer_country"
          placeholder="US"
          value={data.officer_country || ""}
          onChange={(e) => updateField("officer_country", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="effective_date">
            Effective Date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="effective_date"
            type="date"
            value={data.effective_date || new Date().toISOString().split('T')[0]}
            onChange={(e) => updateField("effective_date", e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="board_approval_date">
            Board Approval Date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="board_approval_date"
            type="date"
            value={data.board_approval_date || new Date().toISOString().split('T')[0]}
            onChange={(e) => updateField("board_approval_date", e.target.value)}
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="acceptance_deadline">
          Acceptance Deadline <span className="text-destructive">*</span>
        </Label>
        <Input
          id="acceptance_deadline"
          type="date"
          value={data.acceptance_deadline || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
          onChange={(e) => updateField("acceptance_deadline", e.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Deadline for the officer to sign and return the acceptance letter
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="officer_email">Email (Optional)</Label>
          <Input
            id="officer_email"
            type="email"
            placeholder="jane.smith@email.com"
            value={data.officer_email || ""}
            onChange={(e) => updateField("officer_email", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="officer_phone">Phone (Optional)</Label>
          <Input
            id="officer_phone"
            type="tel"
            placeholder="(555) 987-6543"
            value={data.officer_phone || ""}
            onChange={(e) => updateField("officer_phone", e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="officer_duties">
          Custom Duties & Responsibilities (Optional)
        </Label>
        <Textarea
          id="officer_duties"
          placeholder="Describe any specific duties or responsibilities for this officer position..."
          value={data.officer_duties || ""}
          onChange={(e) => updateField("officer_duties", e.target.value)}
          rows={3}
        />
      </div>

      <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
        This information will be used to generate all required officer appointment documents.
      </div>
    </div>
  );
};
